import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Badge, Card, Skeleton } from '@/components/ui';
import { useAdminWorkers, type WorkerStatus } from '@/hooks/useAdminWorkers';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// /admin/workers — observability page for the long-running workers behind
// the platform. Each card shows: name, reachable badge, uptime, registry
// contents, queues consuming, processed/errors counters, last activity.
// Polled every 10s via useAdminWorkers; same data the platform admin
// would otherwise need to SSH + journalctl for.
// ---------------------------------------------------------------------------
export default function AdminWorkersPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const workers = useAdminWorkers();

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
          {t('aw_title')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1.5">{t('aw_subtitle')}</p>
      </header>

      {workers.isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-[280px]" />
          ))}
        </div>
      ) : workers.error ? (
        <Card padding="lg">
          <p className="text-[14px] text-text-2 font-medium">
            {t('aw_load_error_title')}
          </p>
          <p className="text-[13px] text-text-3 mt-1">
            {workers.error instanceof ApiError
              ? workers.error.message
              : t('aw_load_error_sub')}
          </p>
        </Card>
      ) : (workers.data?.items ?? []).length === 0 ? (
        <Card padding="lg">
          <p className="text-[14px] text-text-2 font-medium">{t('aw_empty_title')}</p>
          <p className="text-[13px] text-text-3 mt-1">{t('aw_empty_sub')}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {workers.data?.items.map((w) => (
            <WorkerCard key={w.name} worker={w} lang={lang} />
          ))}
        </div>
      )}
    </div>
  );
}

interface WorkerCardProps {
  worker: WorkerStatus;
  lang: string;
}
function WorkerCard({ worker, lang }: WorkerCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const tone = workerTone(worker);
  return (
    <Card padding="lg">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="display text-[16px] font-semibold text-text">
            {worker.name}
          </div>
          <div className="mono text-[11px] text-text-3 mt-0.5 truncate" title={worker.url}>
            {worker.url}
          </div>
        </div>
        <StatusBadge tone={tone} />
      </div>

      {!worker.reachable && (
        <div className="px-3 py-2 mb-4 bg-danger-bg border border-danger-bd rounded-md text-[12px] text-danger">
          <strong className="font-semibold">{t('aw_unreachable')}:</strong>{' '}
          {worker.error || t('aw_unknown_error')}
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-[12px]">
        <Stat label={t('aw_field_uptime')} value={formatUptime(worker.uptime_seconds, t)} />
        <Stat
          label={t('aw_field_last_activity')}
          value={
            worker.last_activity_at
              ? formatRelative(worker.last_activity_at, lang)
              : t('aw_never')
          }
        />
        <Stat label={t('aw_field_processed')} value={worker.processed.toLocaleString(lang)} />
        <Stat
          label={t('aw_field_errors')}
          value={worker.errors.toLocaleString(lang)}
          tone={worker.errors > 0 ? 'warn' : undefined}
        />
        <Stat
          label={t('aw_field_goroutines')}
          value={worker.goroutines != null ? String(worker.goroutines) : '—'}
        />
        <Stat label={t('aw_field_latency')} value={`${worker.latency_ms} ms`} />
      </dl>

      {worker.queues && worker.queues.length > 0 && (
        <div className="mt-4 pt-4 border-t border-line-soft">
          <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium mb-2">
            {t('aw_queues_title')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {worker.queues.map((q) => (
              <span
                key={q}
                className="mono text-[11px] px-2 py-0.5 rounded-md bg-surface-2 border border-line text-text-2"
              >
                {q}
              </span>
            ))}
          </div>
        </div>
      )}

      {worker.registry && worker.registry.length > 0 && (
        <div className="mt-4 pt-4 border-t border-line-soft">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium">
              {t('aw_registry_title')}
            </span>
            <span className="text-[11px] num text-text-3">{worker.registry.length}</span>
          </div>
          <ul className="flex flex-col gap-1">
            {worker.registry.map((p) => (
              <li
                key={p.id}
                className="grid grid-cols-[40px_1fr] gap-2 items-center text-[12px]"
              >
                <span className="num text-text-3 mono">#{p.id}</span>
                <span className="text-text">{p.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {worker.go_version && (
        <div className="mt-4 pt-4 border-t border-line-soft text-[11px] text-text-4">
          <span className="mono">{worker.go_version}</span>
        </div>
      )}
    </Card>
  );
}

type Tone = 'ok' | 'warn' | 'danger';

function workerTone(w: WorkerStatus): Tone {
  if (!w.reachable) return 'danger';
  if (w.errors > 0) return 'warn';
  return 'ok';
}

function StatusBadge({ tone }: { tone: Tone }): JSX.Element {
  const { t } = useTranslation('default');
  if (tone === 'danger') {
    return (
      <Badge variant="danger">
        <Icon.warn width="10" height="10" />
        {t('aw_status_down')}
      </Badge>
    );
  }
  if (tone === 'warn') {
    return <Badge variant="warn">{t('aw_status_degraded')}</Badge>;
  }
  return <Badge variant="success">{t('aw_status_up')}</Badge>;
}

interface StatProps {
  label: string;
  value: string;
  tone?: 'warn';
}
function Stat({ label, value, tone }: StatProps): JSX.Element {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.06em] text-text-4 font-medium">
        {label}
      </dt>
      <dd className={cn('text-[13px] num text-text mt-0.5', tone === 'warn' && 'text-warn')}>
        {value}
      </dd>
    </div>
  );
}

// formatUptime turns a second-count into a coarse "Nd Nh Nm" / "Nh Nm" /
// "Nm Ns" depending on magnitude. Goal is glanceable, not precise — the
// admin cares about "is this fresh or has it been running a week" not
// the literal HH:MM:SS.
function formatUptime(seconds: number | undefined, t: (k: string) => string): string {
  if (seconds == null || seconds < 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60) % 60;
  const h = Math.floor(seconds / 3600) % 24;
  const d = Math.floor(seconds / 86400);
  if (d > 0) return `${d}${t('aw_unit_d')} ${h}${t('aw_unit_h')}`;
  if (h > 0) return `${h}${t('aw_unit_h')} ${m}${t('aw_unit_m')}`;
  return `${m}${t('aw_unit_m')} ${seconds % 60}${t('aw_unit_s')}`;
}

// formatRelative returns "5m ago" / "2h ago" / a date for older.
// Standalone helper (not shared with admin-dashboard's because the
// imports are different and inlining keeps the page focused).
function formatRelative(iso: string, lang: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto', style: 'short' });
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  if (diffSec < 3600) return rtf.format(-Math.round(diffSec / 60), 'minute');
  if (diffSec < 86400) return rtf.format(-Math.round(diffSec / 3600), 'hour');
  if (diffSec < 86400 * 7) return rtf.format(-Math.round(diffSec / 86400), 'day');
  return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short' });
}
